import { ArrowLeft } from "lucide-react";
import { Button } from "@carpybara/ui/shadcn/button";
import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ReactNode,
} from "react";

const cx = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(" ");

const DETAIL_BACK_BUTTON_CLASS =
  "group h-10 max-w-full min-w-0 gap-1.5 px-3 text-xs font-normal leading-none lg:h-9";

type CommonProps = {
  children: ReactNode;
  className?: string;
  icon?: ReactNode;
  iconClassName?: string;
  labelClassName?: string;
};

type DetailBackButtonLinkProps = CommonProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "className" | "children"> & {
    href: string;
    prefetch?: boolean;
  };

type DetailBackButtonNativeProps = CommonProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className" | "children"> & {
    href?: undefined;
  };

export type DetailBackButtonProps =
  | DetailBackButtonLinkProps
  | DetailBackButtonNativeProps;

export function DetailBackButton({
  children,
  className,
  icon,
  iconClassName,
  labelClassName,
  ...props
}: DetailBackButtonProps) {
  const content = (
    <>
      <span className={cx("min-w-0 truncate", labelClassName)}>
        {children}
      </span>
    </>
  );

  if ("href" in props && props.href) {
    const { href, prefetch, ...anchorProps } =
      props as DetailBackButtonLinkProps;
    return (
      <Button
        href={href}
        prefetch={prefetch}
        variant="outline"
        size="sm"
        className={cx(DETAIL_BACK_BUTTON_CLASS, className)}
        iconStart={
          icon ?? (
            <ArrowLeft
              className={cx("size-4", iconClassName)}
              aria-hidden
            />
          )
        }
        iconStartMotion="back"
        {...anchorProps}
      >
        {content}
      </Button>
    );
  }

  const { type = "button", ...buttonProps } =
    props as DetailBackButtonNativeProps;
  return (
    <Button
      type={type}
      variant="outline"
      size="sm"
      className={cx(DETAIL_BACK_BUTTON_CLASS, className)}
      iconStart={
        icon ?? (
          <ArrowLeft
            className={cx("size-4", iconClassName)}
            aria-hidden
          />
        )
      }
      iconStartMotion="back"
      {...buttonProps}
    >
      {content}
    </Button>
  );
}
